import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { fetchCurrentUser } from '../../ducks/user.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { getLineLinkSessionStatusApi, getLineStatusApi } from '../../util/api';
import { useIntl } from '../../util/reactIntl';
import {
  clearPendingLineLinkSession,
  getCurrentLineReturnTo,
  getLineConnectResultFromSearch,
  isLineLinkSessionExpired,
  readPendingLineLinkSession,
  sanitizeLineReturnTo,
  stripLineConnectParamsFromSearch,
  writePendingLineLinkSession,
} from '../../util/lineConnectFlow';
import {
  isLineAccountConnected,
  shouldPromptForLineConnect as shouldPromptForLineConnectStatus,
} from '../../util/lineNotifications';

import LineConnectModal from './LineConnectModal';

const noop = () => {};

const LineConnectModalContext = createContext({
  isLineConnectModalOpen: false,
  lineConnectionStatus: null,
  lineConnectionStatusLoading: false,
  lineConnectionStatusLoaded: false,
  lineConnectionStatusError: null,
  shouldPromptForLineConnect: false,
  refreshLineConnectionStatus: () => Promise.resolve(null),
  openLineConnectModal: noop,
  closeLineConnectModal: noop,
});

const getUserId = currentUser => {
  const id = currentUser?.id;
  return typeof id === 'string' ? id : id?.uuid || null;
};

const normalizeModalOptions = options => {
  const returnTo = sanitizeLineReturnTo(options?.returnTo || getCurrentLineReturnTo());
  const source = options?.source || 'manual';

  return {
    ...options,
    open: true,
    returnTo,
    source,
    context: options?.context || source,
    role: options?.role || null,
    initialSession: options?.initialSession || null,
  };
};

const buildPendingSession = ({ session, modalConfig }) => {
  const sessionId = session?.sessionId;
  if (!sessionId) {
    return null;
  }

  const returnTo = sanitizeLineReturnTo(
    session.returnTo || modalConfig?.returnTo || getCurrentLineReturnTo()
  );

  return {
    sessionId,
    returnTo,
    source: session.source || modalConfig?.source || 'manual',
    role: session.role || modalConfig?.role || null,
    createdAt: session.createdAt || new Date().toISOString(),
    expiresAt: session.expiresAt || null,
    session: {
      ...session,
      returnTo,
      source: session.source || modalConfig?.source || 'manual',
      role: session.role || modalConfig?.role || null,
    },
  };
};

const toastForLineConnectResult = (status, intl) => {
  if (status === 'success') {
    toast.success(intl.formatMessage({ id: 'LineConnectModal.toastSuccess' }));
  } else if (status === 'expired') {
    toast.warn(intl.formatMessage({ id: 'LineConnectModal.toastExpired' }));
  } else if (status === 'cancelled') {
    toast.info(intl.formatMessage({ id: 'LineConnectModal.toastCancelled' }));
  } else {
    toast.error(intl.formatMessage({ id: 'LineConnectModal.toastError' }));
  }
};

export const LineConnectModalProvider = props => {
  const { children } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.user?.currentUser);
  const currentUserId = getUserId(currentUser);
  const history = useHistory();
  const location = useLocation();
  const [modalConfig, setModalConfig] = useState(null);
  const [lineStatusState, setLineStatusState] = useState({
    userId: null,
    phase: 'idle',
    status: null,
    error: null,
  });
  const modalConfigRef = useRef(modalConfig);
  const currentUserIdRef = useRef(currentUserId);
  const lineStatusRequestRef = useRef(null);
  const lineStatusRequestVersionRef = useRef(0);
  const recoveredPendingSessionRef = useRef(null);

  currentUserIdRef.current = currentUserId;

  useEffect(() => {
    modalConfigRef.current = modalConfig;
  }, [modalConfig]);

  const refreshCurrentUser = useCallback(() => {
    return dispatch(fetchCurrentUser({ enforce: true, updateNotifications: false }));
  }, [dispatch]);

  const refreshLineConnectionStatus = useCallback(
    (options = {}) => {
      const { force = false } = options;
      const requestedUserId = currentUserId;
      if (!requestedUserId) {
        return Promise.resolve(null);
      }

      const activeRequest = lineStatusRequestRef.current;
      if (!force && activeRequest?.userId === requestedUserId) {
        return activeRequest.promise;
      }

      const requestVersion = lineStatusRequestVersionRef.current + 1;
      lineStatusRequestVersionRef.current = requestVersion;
      setLineStatusState({
        userId: requestedUserId,
        phase: 'loading',
        status: null,
        error: null,
      });

      const request = getLineStatusApi()
        .then(status => {
          const isCurrentRequest =
            currentUserIdRef.current === requestedUserId &&
            lineStatusRequestVersionRef.current === requestVersion;
          if (isCurrentRequest) {
            setLineStatusState({
              userId: requestedUserId,
              phase: 'loaded',
              status,
              error: null,
            });
          }
          return isCurrentRequest ? status : null;
        })
        .catch(error => {
          if (
            currentUserIdRef.current === requestedUserId &&
            lineStatusRequestVersionRef.current === requestVersion
          ) {
            setLineStatusState({
              userId: requestedUserId,
              phase: 'error',
              status: null,
              error,
            });
          }
          return null;
        })
        .finally(() => {
          if (lineStatusRequestRef.current?.version === requestVersion) {
            lineStatusRequestRef.current = null;
          }
        });

      lineStatusRequestRef.current = {
        userId: requestedUserId,
        version: requestVersion,
        promise: request,
      };
      return request;
    },
    [currentUserId]
  );

  const openLineConnectModal = useCallback(
    (options = {}) => {
      if (!options.requireLatestConnectionStatus) {
        setModalConfig(normalizeModalOptions(options));
        return Promise.resolve(true);
      }

      const requestedUserId = currentUserId;
      return refreshLineConnectionStatus({ force: true }).then(status => {
        if (
          !status ||
          currentUserIdRef.current !== requestedUserId ||
          !shouldPromptForLineConnectStatus(status)
        ) {
          return false;
        }

        setModalConfig(normalizeModalOptions(options));
        return true;
      });
    },
    [currentUserId, refreshLineConnectionStatus]
  );

  const closeLineConnectModal = useCallback(() => {
    const currentConfig = modalConfigRef.current;
    setModalConfig(null);
    currentConfig?.onClose?.();
  }, []);

  const handleManageDisableScrolling = useCallback(
    (componentId, disableScrolling) => {
      dispatch(manageDisableScrolling(componentId, disableScrolling));
    },
    [dispatch]
  );

  const persistPendingSession = useCallback(session => {
    const pendingSession = buildPendingSession({
      session,
      modalConfig: modalConfigRef.current,
    });

    if (pendingSession) {
      writePendingLineLinkSession(pendingSession);
    }
  }, []);

  const handleStatusChanged = useCallback(
    status => {
      const currentConfig = modalConfigRef.current;
      const connected = status?.status === 'connected' || status?.connected;

      if (connected) {
        clearPendingLineLinkSession();
        setModalConfig(null);
        refreshCurrentUser();
      }

      refreshLineConnectionStatus({ force: true });

      currentConfig?.onStatusChanged?.(status);
    },
    [refreshCurrentUser, refreshLineConnectionStatus]
  );

  useEffect(() => {
    if (!currentUserId) {
      lineStatusRequestVersionRef.current += 1;
      lineStatusRequestRef.current = null;
      recoveredPendingSessionRef.current = null;
      setLineStatusState({ userId: null, phase: 'idle', status: null, error: null });
      setModalConfig(null);
      return;
    }

    refreshLineConnectionStatus({ force: true });
  }, [currentUserId, refreshLineConnectionStatus]);

  useEffect(() => {
    const result = getLineConnectResultFromSearch(location.search);
    if (!result) {
      return;
    }

    if (result.status === 'success') {
      clearPendingLineLinkSession();
      setModalConfig(null);
      refreshCurrentUser();
      refreshLineConnectionStatus({ force: true });
    }

    toastForLineConnectResult(result.status, intl);

    const nextSearch = stripLineConnectParamsFromSearch(location.search);
    history.replace({
      pathname: location.pathname,
      search: nextSearch,
      hash: location.hash,
      state: location.state,
    });
  }, [
    history,
    intl,
    location.hash,
    location.pathname,
    location.search,
    location.state,
    refreshCurrentUser,
    refreshLineConnectionStatus,
  ]);

  useEffect(() => {
    if (!currentUserId || recoveredPendingSessionRef.current === currentUserId) {
      return;
    }
    recoveredPendingSessionRef.current = currentUserId;

    const pendingSession = readPendingLineLinkSession();
    if (!pendingSession?.sessionId) {
      return;
    }

    if (isLineLinkSessionExpired(pendingSession)) {
      clearPendingLineLinkSession();
      return;
    }

    refreshLineConnectionStatus()
      .then(connectionStatus => {
        if (!connectionStatus || isLineAccountConnected(connectionStatus)) {
          if (isLineAccountConnected(connectionStatus)) {
            clearPendingLineLinkSession();
          }
          return null;
        }
        return getLineLinkSessionStatusApi(pendingSession.sessionId);
      })
      .then(status => {
        if (!status) {
          return;
        }
        if (status?.status === 'connected' || status?.connected) {
          clearPendingLineLinkSession();
          refreshCurrentUser();
          refreshLineConnectionStatus({ force: true });
          toastForLineConnectResult('success', intl);
          return;
        }

        if (status?.status === 'expired') {
          clearPendingLineLinkSession();
          toastForLineConnectResult('expired', intl);
          return;
        }

        if (!modalConfigRef.current) {
          openLineConnectModal({
            source: pendingSession.source || 'manual',
            role: pendingSession.role || null,
            returnTo: pendingSession.returnTo,
            initialSession: pendingSession.session || pendingSession,
            requireLatestConnectionStatus: true,
          });
        }
      })
      .catch(() => {
        // If the user is logged out or the status endpoint is unavailable, leave the app route alone.
      });
  }, [currentUserId, intl, openLineConnectModal, refreshCurrentUser, refreshLineConnectionStatus]);

  const hasCurrentUserLineStatus = lineStatusState.userId === currentUserId;
  const lineConnectionStatusLoaded =
    !!currentUserId && hasCurrentUserLineStatus && lineStatusState.phase === 'loaded';
  const lineConnectionStatusLoading =
    !!currentUserId && (!hasCurrentUserLineStatus || lineStatusState.phase === 'loading');
  const lineConnectionStatus = lineConnectionStatusLoaded ? lineStatusState.status : null;
  const lineConnectionStatusError =
    hasCurrentUserLineStatus && lineStatusState.phase === 'error' ? lineStatusState.error : null;
  const shouldPromptForLineConnect =
    lineConnectionStatusLoaded && shouldPromptForLineConnectStatus(lineConnectionStatus);

  const providerValue = useMemo(
    () => ({
      isLineConnectModalOpen: !!modalConfig?.open,
      lineConnectionStatus,
      lineConnectionStatusLoading,
      lineConnectionStatusLoaded,
      lineConnectionStatusError,
      shouldPromptForLineConnect,
      refreshLineConnectionStatus,
      openLineConnectModal,
      closeLineConnectModal,
    }),
    [
      closeLineConnectModal,
      lineConnectionStatus,
      lineConnectionStatusError,
      lineConnectionStatusLoaded,
      lineConnectionStatusLoading,
      modalConfig?.open,
      openLineConnectModal,
      refreshLineConnectionStatus,
      shouldPromptForLineConnect,
    ]
  );

  return (
    <LineConnectModalContext.Provider value={providerValue}>
      {children}
      <LineConnectModal
        id="GlobalLineConnectModal"
        isOpen={!!modalConfig?.open}
        onClose={closeLineConnectModal}
        onManageDisableScrolling={handleManageDisableScrolling}
        onSessionReady={persistPendingSession}
        onLineActionStarted={persistPendingSession}
        onStatusChanged={handleStatusChanged}
        onSkip={modalConfig?.onSkip}
        onDontAskAgain={modalConfig?.onDontAskAgain}
        role={modalConfig?.role}
        context={modalConfig?.context}
        returnTo={modalConfig?.returnTo}
        source={modalConfig?.source}
        initialSession={modalConfig?.initialSession}
      />
    </LineConnectModalContext.Provider>
  );
};

export const useLineConnectModal = () => useContext(LineConnectModalContext);

export default LineConnectModalProvider;
